import { ref, computed } from '@vue/composition-api'
import { useGetActiveCustomers } from '../services/getActiveCustomersService'

/**
 * Composable to fetch and format active customers for select options
 * @param {Ref<String>|String} companyId - Company ID (can be ref or string)
 * @param {Object} options - Additional options
 * @param {String} options.searchTerm - Optional search term to filter customers
 * @param {String} options.sortBy - Field to sort by (default: 'customer_id')
 * @param {String} options.sortOrder - Sort order 'asc' or 'desc' (default: 'asc')
 */
export const useActiveCustomers = (companyId, options = {}) => {
	const searchTerm = ref(options.searchTerm || '')
	const sortBy = ref(options.sortBy || 'customer_id')
	const sortOrder = ref(options.sortOrder || 'asc')

	// Use the service to fetch active customers
	const { customers, customersCount, loading, error, executeQuery, refetch } =
		useGetActiveCustomers(companyId)

	// Format customer display text
	const formatCustomerDisplay = (customer) => {
		if (!customer) return ''

		let display = `${customer.customer_id} - ${customer.customer_title}`
		if (customer.customer_nickname) {
			display += ` (${customer.customer_nickname})`
		}
		return display
	}

	// Format customers for select options
	const formattedCustomerOptions = computed(() => {
		if (!customers.value) return []

		let filtered = [...customers.value]

		// Apply search filter if search term exists
		if (searchTerm.value) {
			const term = searchTerm.value.toLowerCase()
			filtered = filtered.filter((customer) => {
				return (
					customer.customer_id.toLowerCase().includes(term) ||
					customer.customer_title.toLowerCase().includes(term) ||
					(customer.customer_nickname &&
						customer.customer_nickname.toLowerCase().includes(term))
				)
			})
		}

		// Sort customers
		filtered.sort((a, b) => {
			const aVal = a[sortBy.value] || ''
			const bVal = b[sortBy.value] || ''

			if (sortOrder.value === 'asc') {
				return aVal.localeCompare(bVal)
			} else {
				return bVal.localeCompare(aVal)
			}
		})

		// Map to select option format
		return filtered.map((customer) => ({
			// Original data
			customer_id: customer.customer_id,
			customer_title: customer.customer_title,
			customer_nickname: customer.customer_nickname,
			// Formatted display text for select option
			displayText: formatCustomerDisplay(customer),
		}))
	})

	// Get a single customer by ID
	const getCustomerById = (customerId) => {
		return formattedCustomerOptions.value.find(
			(customer) => customer.customer_id === customerId,
		)
	}

	// Check if there are customers available
	const hasCustomers = computed(() => {
		return formattedCustomerOptions.value.length > 0
	})

	// Message when no customers are available
	const noCustomersMessage = computed(() => {
		if (loading.value) return 'Loading customers...'
		if (error.value) return 'Error loading customers'
		if (!hasCustomers.value) {
			if (searchTerm.value) {
				return `No customers found matching "${searchTerm.value}"`
			}
			return 'No active customers available'
		}
		return ''
	})

	// Update search term
	const setSearchTerm = (term) => {
		searchTerm.value = term
	}

	// Update sort options
	const setSortOptions = (field, order = 'asc') => {
		sortBy.value = field
		sortOrder.value = order
	}

	// Clear all filters
	const clearFilters = () => {
		searchTerm.value = ''
		sortBy.value = 'customer_id'
		sortOrder.value = 'asc'
	}

	// Refresh the customers list
	const refreshCustomers = async () => {
		try {
			await refetch()
		} catch (err) {
			console.error('Error refreshing customers:', err)
		}
	}

	return {
		// Main data
		formattedCustomerOptions,
		customersCount,
		// Loading states
		loading,
		error,
		// Helper functions
		getCustomerById,
		formatCustomerDisplay,
		// Search and filter
		searchTerm,
		setSearchTerm,
		// Sort
		sortBy,
		sortOrder,
		setSortOptions,
		// Utility
		hasCustomers,
		noCustomersMessage,
		clearFilters,
		// Actions
		refreshCustomers,
		executeQuery,
	}
}

/**
 * Alternative standalone function to get formatted customer options
 * Useful for one-time fetches without reactive updates
 */
export const getFormattedCustomerOptions = async (apolloClient, companyId) => {
	const { getActiveCustomers } = await import(
		'../services/getActiveCustomersService'
	)

	try {
		const customers = await getActiveCustomers(apolloClient, companyId)

		// Format customers for select options
		const formattedOptions = customers.map((customer) => {
			let displayText = `${customer.customer_id} - ${customer.customer_title}`
			if (customer.customer_nickname) {
				displayText += ` (${customer.customer_nickname})`
			}

			return {
				customer_id: customer.customer_id,
				customer_title: customer.customer_title,
				customer_nickname: customer.customer_nickname,
				displayText,
			}
		})

		// Sort by customer_id by default
		formattedOptions.sort((a, b) => {
			return a.customer_id.localeCompare(b.customer_id)
		})

		return formattedOptions
	} catch (error) {
		console.error('Error getting formatted customer options:', error)
		throw error
	}
}
